import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import './styles';
import { AvisoTrialContainer } from './styles';
import { useTheme } from '../../contexts/Theme/ThemeContext';
import { EmpresaApi } from '../../hooks/empresaApi';
import { AssinaturaApi } from '../../hooks/assinaturaApi';
import { InfoAssinatura } from '../../types/Assinatura';

interface AvisoTrialProps {}

/**
 * Faixa de status de pagamento no topo do painel.
 * Prioridade: modo_restrito (vermelho+link) > pagamento_pendente (amarelo+link)
 * > atraso legado (vermelho) > trial (verde) > nada.
 * Fluxo novo usa info.assinatura.status; legado usa getDiasTesteGratis.
 */
const AvisoTrial: React.FC<AvisoTrialProps> = () => {
   const { theme } = useTheme();
   const location = useLocation();
   const [diasTrialLegado, setDiasTrialLegado] = useState<number | null>(null);
   const [pagamentoEmAtrasoLegado, setPagamentoEmAtrasoLegado] = useState<boolean>(false);
   const [info, setInfo] = useState<InfoAssinatura | null>(null);
   const [carregou, setCarregou] = useState<boolean>(false);

   useEffect(() => {
      const carregar = async () => {
         // 2 chamadas em paralelo — uma pra cada fluxo
         const [tr, novo] = await Promise.allSettled([
            EmpresaApi().getDiasTesteGratis(),
            AssinaturaApi().getInfoAssinatura(),
         ]);

         if (tr.status === 'fulfilled' && tr.value && tr.value.dias_restantes !== undefined) {
            setDiasTrialLegado(tr.value.dias_restantes);
            setPagamentoEmAtrasoLegado(tr.value.pagamento_em_atraso || false);
         }
         if (novo.status === 'fulfilled') {
            setInfo(novo.value);
         }
         setCarregou(true);
      };
      carregar();
   }, []);

   if (!carregou) {
      return null;
   }

   const fluxoNovo = info?.fluxo_novo === true;

   // 1. Fluxo novo: modo_restrito (mais grave). Na /assinatura a tela já mostra
   //    o painel vermelho de acesso restrito — não duplica no topo.
   if (fluxoNovo && info?.modo_restrito && location.pathname !== '/assinatura') {
      return (
         <AvisoTrialContainer theme={theme} alerta>
            <div className="faixa">
               <div className="texto fonteTexto">
                  Identificamos um pagamento em aberto na sua assinatura. Regularize abaixo para continuar com acesso completo.
               </div>
            </div>
         </AvisoTrialContainer>
      );
   }

   // 2. Fluxo novo: pagamento_pendente (ativo=1 com ciclo vencido). Na /assinatura
   //    a própria tela já mostra o painel vermelho de pagamento pendente — não duplica.
   if (fluxoNovo && info?.pagamento_pendente && location.pathname !== '/assinatura') {
      return (
         <AvisoTrialContainer theme={theme} aviso>
            <div className="faixa">
               <div className="texto fonteTexto">
                  <strong>Tivemos um problema com seu último pagamento.</strong>
                  <Link to="/assinatura" className="link-regularizar">
                     Regularizar agora
                  </Link>
               </div>
            </div>
         </AvisoTrialContainer>
      );
   }

   // 2.5 Fluxo novo: cartão pendente (forma cartão sem card_id) — sem dívida,
   //     só avisa pra atualizar antes da próxima cobrança. Vem depois de
   //     modo_restrito/pagamento_pendente, que já retornaram (não duplica).
   if (fluxoNovo && info?.cartao_pendente) {
      return (
         <AvisoTrialContainer theme={theme} aviso>
            <div className="faixa">
               <div className="texto fonteTexto">
                  <strong>Identificamos um problema com as informações de pagamento.</strong>{' '}
                  Atualize os dados do seu cartão para garantir sua próxima cobrança.
                  <Link to="/assinatura" className="link-regularizar">
                     Atualizar cartão
                  </Link>
               </div>
            </div>
         </AvisoTrialContainer>
      );
   }

   // 3. Fluxo novo em trial — calcula dias localmente
   if (fluxoNovo && info?.assinatura?.status === 'trial') {
      const proximoVenc = info.assinatura.proximo_vencimento;
      if (!proximoVenc) return null;
      const dias = moment(proximoVenc, 'YYYY-MM-DD').startOf('day')
         .diff(moment().startOf('day'), 'days');
      if (dias <= 0) return null; // trial novo já acabou — Job A vai criar ciclo
      return (
         <AvisoTrialContainer theme={theme}>
            <div className="faixa">
               <div className="texto fonteTexto">
                  <strong>
                     {dias === 1
                        ? `Falta ${dias} dia para acabar seu teste grátis.`
                        : `Faltam ${dias} dias para acabar seu teste grátis.`}
                  </strong>{' '}
                  A cobrança será feita automaticamente no fim do período.
               </div>
            </div>
         </AvisoTrialContainer>
      );
   }

   // 4. Fluxo novo em outros estados (ativa/em_atraso/suspensa/cancelada) — nada
   //    (modo_restrito e pagamento_pendente já cobertos acima)
   if (fluxoNovo) {
      return null;
   }

   // === Daqui pra baixo: empresas legadas (Superlogica) ===

   if (diasTrialLegado === null) {
      return null;
   }

   // 3-legado. Pagamento em atraso (Superlogica)
   if (diasTrialLegado <= 0 && pagamentoEmAtrasoLegado) {
      return (
         <AvisoTrialContainer theme={theme} alerta>
            <div className="faixa">
               <div className="texto fonteTexto">
                  <strong>Regularize o pagamento para evitar o bloqueio de sua conta.</strong>
               </div>
            </div>
         </AvisoTrialContainer>
      );
   }

   // 4-legado. Trial expirado + pagamento ok — nada
   if (diasTrialLegado <= 0) {
      return null;
   }

   // 5-legado. Trial em andamento (Superlogica)
   return (
      <AvisoTrialContainer theme={theme}>
         <div className="faixa">
            <div className="texto fonteTexto">
               <strong>
                  {diasTrialLegado === 1
                     ? `Falta ${diasTrialLegado} dia para acabar seu teste grátis.`
                     : `Faltam ${diasTrialLegado} dias para acabar seu teste grátis.`}
               </strong>{' '}
               A cobrança será feita automaticamente no fim do período.
            </div>
         </div>
      </AvisoTrialContainer>
   );
};

export default AvisoTrial;
